import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { runParamSweep, createBacktest, runBacktest } from '../../api';
import { extractNumericParams } from './utils';
import { useBackgroundRuns } from '../../context/BackgroundRunsContext';

const MAX_PARAMS = 2;
const MAX_TICKS = 25;
const DEFAULT_TICKS = 10;

// 'data' and 'correlation' nodes drive which external series get fetched for
// the whole run -- sweeping their fields wouldn't be reflected correctly
// since the backend fetches OHLCV/external data once and reuses it across
// every grid point for speed. Keep them off the sweepable list.
const SWEEPABLE_NODE_TYPES = new Set([
  'indicator', 'action', 'condition', 'value', 'lookback', 'timeframe',
  'fibonacci', 'trendline', 'pattern', 'volatility', 'position_age', 'time',
]);

const METRIC_OPTIONS = [
  { key: 'return_pct', label: 'Return %', higherIsBetter: true, fmt: (v) => (v == null ? '—' : `${v.toFixed(1)}%`) },
  { key: 'sharpe_ratio', label: 'Sharpe', higherIsBetter: true, fmt: (v) => (v == null ? '—' : v.toFixed(2)) },
  { key: 'max_drawdown_pct', label: 'Max Drawdown %', higherIsBetter: false, fmt: (v) => (v == null ? '—' : `${v.toFixed(1)}%`) },
  { key: 'total_trades', label: 'Total Trades', higherIsBetter: true, fmt: (v) => (v == null ? '—' : String(v)) },
];

function paramDisplayLabel(p) {
  const nodeLabel = p.nodeLabel || p.nodeType;
  const leaf = p.path[p.path.length - 1];
  const leafLabel = String(leaf).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return `${nodeLabel} — ${leafLabel}`;
}

// Default sweep range: +/-50% around the current value, 10 evenly-spaced
// ticks. Integer-looking values (periods, windows, bar counts -- the vast
// majority of strategy parameters) stay integers; everything else keeps 2
// decimal places. This is a starting point the user can freely override.
function defaultRangeFor(param) {
  const val = param.value;
  const isIntLike = Number.isInteger(val) && val >= 1;
  let min = val * 0.5;
  let max = val * 1.5;
  if (isIntLike) {
    min = Math.max(1, Math.round(min));
    max = Math.max(min + 1, Math.round(max));
  } else {
    min = Math.round(min * 100) / 100;
    max = Math.round(max * 100) / 100;
    if (max <= min) max = min + 1;
  }
  return { min, max, ticks: DEFAULT_TICKS, isIntLike };
}

function buildValues(min, max, ticks, isIntLike) {
  const n = Math.max(2, Math.min(MAX_TICKS, Math.round(ticks) || DEFAULT_TICKS));
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  const step = (hi - lo) / (n - 1);
  const raw = Array.from({ length: n }, (_, i) => lo + step * i);
  const rounded = raw.map((v) => (isIntLike ? Math.round(v) : Math.round(v * 100) / 100));
  return Array.from(new Set(rounded)).sort((a, b) => a - b);
}

function heatColor(value, min, max, higherIsBetter) {
  if (value == null || min == null || max == null || max === min) return 'transparent';
  const t = (value - min) / (max - min);
  const norm = higherIsBetter ? t : 1 - t;
  const hue = 0 + norm * 142; // red (0) -> green (142)
  return `hsla(${hue}, 65%, 45%, ${0.15 + norm * 0.45})`;
}

export default function ParameterSweepPanel({ backtest, strategyForEdit, onRerunResult }) {
  const [slots, setSlots] = useState([null, null]); // each: { key, ...rangeConfig } | null
  const [metricKey, setMetricKey] = useState('return_pct');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [sweepData, setSweepData] = useState(backtest?.results?.param_sweep || null);
  const { startRun } = useBackgroundRuns();

  const allParams = useMemo(
    () => extractNumericParams(strategyForEdit?.graph).filter((p) => SWEEPABLE_NODE_TYPES.has(p.nodeType)),
    [strategyForEdit],
  );
  const paramsByKey = useMemo(() => {
    const m = {};
    allParams.forEach((p) => { m[p.key] = p; });
    return m;
  }, [allParams]);

  useEffect(() => {
    setSweepData(backtest?.results?.param_sweep || null);
    setError('');
    setInfo('');
  }, [backtest?.id, backtest?.results?.param_sweep]);

  const setSlotParam = (idx, key) => {
    setSlots((prev) => {
      const next = [...prev];
      if (!key) {
        next[idx] = null;
      } else {
        const param = paramsByKey[key];
        const range = defaultRangeFor(param);
        next[idx] = { key, baseLabel: paramDisplayLabel(param), nodeId: param.nodeId, ...range };
      }
      // The label doubles as the object key for each grid point's value, so it
      // MUST be unique across the (at most 2) active slots -- two SMA "Period"
      // fields would otherwise collide onto one key and silently drop a whole
      // dimension of the sweep. Disambiguate with the node id whenever needed.
      return next.map((slot, i) => {
        if (!slot) return slot;
        const other = next[i === 0 ? 1 : 0];
        const collides = other && other.baseLabel === slot.baseLabel;
        return { ...slot, label: collides ? `${slot.baseLabel} (node ${slot.nodeId})` : slot.baseLabel };
      });
    });
  };

  const updateSlotField = (idx, field, value) => {
    setSlots((prev) => {
      const next = [...prev];
      if (!next[idx]) return prev;
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const activeSlots = slots.filter(Boolean);
  const totalCombos = activeSlots.reduce((acc, s) => {
    const vals = buildValues(Number(s.min), Number(s.max), Number(s.ticks), s.isIntLike);
    return acc * Math.max(1, vals.length);
  }, 1);

  const handleRunSweep = async () => {
    if (!backtest?.id || activeSlots.length === 0) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const parameters = activeSlots.map((s) => {
        const param = paramsByKey[s.key];
        const values = buildValues(Number(s.min), Number(s.max), Number(s.ticks), s.isIntLike);
        return {
          node_id: param.nodeId,
          path: param.path.join('.'),
          label: s.label,
          values,
        };
      });
      const resp = await runParamSweep(backtest.id, { parameters });
      if (resp?.data?.queued) {
        setInfo(resp.data.detail || 'Parameter sweep queued in the background.');
        startRun({
          backtestId: backtest.id,
          kind: 'param_sweep',
          onComplete: (data) => {
            setSweepData(data?.results?.param_sweep || null);
            setInfo(`Sweep completed — ${data?.results?.param_sweep?.grid?.length ?? 0} combinations.`);
          },
        });
        return;
      }
      setSweepData(resp.data?.param_sweep || null);
      setInfo(`Sweep completed — ${resp.data?.param_sweep?.grid?.length ?? 0} combinations.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to run parameter sweep.');
    } finally {
      setBusy(false);
    }
  };

  const handleApplyPoint = async (point) => {
    if (!sweepData?.parameters || !onRerunResult) return;
    const overrides = {};
    sweepData.parameters.forEach((p) => {
      overrides[`${p.node_id}:${p.path}`] = point[p.label];
    });
    // Reuse the existing "run with param_overrides" preview flow -- same
    // mechanism QuickEdit/WFA-optimize already use to preview a parameter
    // set without permanently saving it to the strategy.
    setBusy(true);
    setError('');
    try {
      const createResp = await createBacktest({
        strategy: strategyForEdit?.id || backtest.strategy_id || backtest.strategy,
        asset_type: backtest.asset_type,
        symbol: backtest.symbol,
        start_date: backtest.start_date,
        end_date: backtest.end_date,
        initial_cash: backtest.initial_cash,
        slippage_perc: backtest.slippage_perc,
        commission_perc: backtest.commission_perc,
        fill_on_close: backtest.fill_on_close,
        fx_interval: backtest.fx_interval,
      });
      const runResp = await runBacktest(createResp.data.id, { param_overrides: overrides });
      if (runResp?.data?.queued) {
        setInfo(runResp.data.detail || 'Preview queued in the background.');
      } else {
        onRerunResult(runResp.data);
        setInfo('Applied this combination to a preview backtest result.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to apply this combination.');
    } finally {
      setBusy(false);
    }
  };

  const metric = METRIC_OPTIONS.find((m) => m.key === metricKey) || METRIC_OPTIONS[0];
  const grid = sweepData?.grid || [];
  const sweepParams = sweepData?.parameters || [];

  const metricValues = grid.map((g) => g[metric.key]).filter((v) => v != null);
  const metricMin = metricValues.length ? Math.min(...metricValues) : null;
  const metricMax = metricValues.length ? Math.max(...metricValues) : null;

  let bestPoint = null;
  grid.forEach((g) => {
    const v = g[metric.key];
    if (v == null) return;
    if (!bestPoint) { bestPoint = g; return; }
    const better = metric.higherIsBetter ? v > bestPoint[metric.key] : v < bestPoint[metric.key];
    if (better) bestPoint = g;
  });

  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: 14,
      marginBottom: 24,
    }}>
      <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Parameter Sweep</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
        Run this backtest across a grid of 1-2 numeric parameters to see how sensitive the result is —
        a tight cluster of good results means a robust setting; one lucky spike means it's probably overfit.
      </div>

      {Array.from({ length: MAX_PARAMS }, (_, i) => i).map((idx) => {
        const slot = slots[idx];
        const otherKey = slots[idx === 0 ? 1 : 0]?.key;
        return (
          <div key={idx} style={{ marginBottom: 10, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 70 }}>
                Parameter {idx + 1}{idx === 1 ? ' (optional)' : ''}
              </span>
              <select
                value={slot?.key || ''}
                onChange={(e) => setSlotParam(idx, e.target.value || null)}
                style={{ flex: 1, minWidth: 180, background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px' }}
              >
                <option value="">{idx === 1 ? '— none —' : '— select a parameter —'}</option>
                {allParams.map((p) => (
                  <option key={p.key} value={p.key} disabled={p.key === otherKey}>
                    {paramDisplayLabel(p)} (current: {p.value})
                  </option>
                ))}
              </select>
            </div>

            {slot && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)' }}>
                  Min
                  <input
                    type="number"
                    value={slot.min}
                    onChange={(e) => updateSlotField(idx, 'min', e.target.value)}
                    style={{ width: 70, background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 7px' }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)' }}>
                  Max
                  <input
                    type="number"
                    value={slot.max}
                    onChange={(e) => updateSlotField(idx, 'max', e.target.value)}
                    style={{ width: 70, background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 7px' }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--muted)' }}>
                  Ticks
                  <input
                    type="number"
                    min={2}
                    max={MAX_TICKS}
                    value={slot.ticks}
                    onChange={(e) => updateSlotField(idx, 'ticks', e.target.value)}
                    style={{ width: 55, background: 'var(--card)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 7px' }}
                  />
                </label>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                  → {buildValues(Number(slot.min), Number(slot.max), Number(slot.ticks), slot.isIntLike).join(', ')}
                </span>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>
          {activeSlots.length > 0 ? `${totalCombos} backtest${totalCombos !== 1 ? 's' : ''} in this sweep` : 'Select at least one parameter'}
        </span>
        <button
          onClick={handleRunSweep}
          disabled={busy || activeSlots.length === 0}
          style={{
            marginLeft: 'auto',
            background: busy ? 'var(--subtle)' : 'var(--teal)',
            color: 'var(--bg)',
            border: 'none',
            borderRadius: 8,
            padding: '8px 12px',
            fontWeight: 700,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          {busy ? 'Running…' : (sweepData ? 'Re-run Sweep' : 'Run Sweep')}
        </button>
      </div>

      {error && <div style={{ marginTop: 8, color: 'var(--red)', fontSize: 12 }}>{error}</div>}
      {info && <div style={{ marginTop: 8, color: 'var(--teal)', fontSize: 12 }}>{info}</div>}

      {grid.length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Color / rank by:</span>
            <select
              value={metricKey}
              onChange={(e) => setMetricKey(e.target.value)}
              style={{ background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 8px' }}
            >
              {METRIC_OPTIONS.map((m) => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
            {bestPoint && (
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                Best: {sweepParams.map((p) => `${p.label}=${bestPoint[p.label]}`).join(', ')} → {metric.fmt(bestPoint[metric.key])}
              </span>
            )}
          </div>

          {sweepParams.length === 1 && (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={grid} margin={{ top: 10, right: 10, bottom: 0, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey={sweepParams[0].label} tick={{ fontSize: 11, fill: 'var(--muted)' }} label={{ value: sweepParams[0].label, position: 'insideBottom', offset: -2, fontSize: 10, fill: 'var(--muted)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--muted)' }} domain={['auto', 'auto']} tickFormatter={(v) => metric.fmt(v)} />
                <Tooltip
                  formatter={(val) => [metric.fmt(val), metric.label]}
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
                />
                <Line type="monotone" dataKey={metric.key} stroke="var(--teal)" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}

          {sweepParams.length === 2 && (() => {
            const [pA, pB] = sweepParams;
            const aVals = pA.values;
            const bVals = pB.values;
            const cell = {};
            grid.forEach((g) => {
              cell[`${g[pA.label]}|${g[pB.label]}`] = g;
            });
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--muted)' }}>
                        {pB.label} \ {pA.label}
                      </th>
                      {aVals.map((a) => (
                        <th key={a} style={{ padding: '6px 10px', textAlign: 'center', color: 'var(--text)', fontSize: 11 }}>{a}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bVals.map((b) => (
                      <tr key={b}>
                        <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--text)', fontSize: 11, whiteSpace: 'nowrap' }}>{b}</td>
                        {aVals.map((a) => {
                          const pt = cell[`${a}|${b}`];
                          const v = pt ? pt[metric.key] : null;
                          const isBest = bestPoint && pt === bestPoint;
                          return (
                            <td
                              key={a}
                              title={pt?.error || ''}
                              onClick={() => pt && !pt.error && handleApplyPoint(pt)}
                              style={{
                                padding: '6px 10px',
                                textAlign: 'center',
                                background: heatColor(v, metricMin, metricMax, metric.higherIsBetter),
                                color: 'var(--text)',
                                fontWeight: isBest ? 700 : 400,
                                border: isBest ? '2px solid var(--teal)' : '1px solid var(--border)',
                                cursor: pt && !pt.error ? 'pointer' : 'default',
                              }}
                            >
                              {pt?.error ? '⚠' : metric.fmt(v)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
                  Click a cell to preview that combination in the results above.
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
